import React, { Suspense } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { EmptyState } from '../../../components/ui/EmptyState';

const ItemDetailPage = React.lazy(() => import('../items/ItemDetailPage'));
const KitDetailPage = React.lazy(() => import('../kits/KitDetailPage'));
const PoDetailPage = React.lazy(() => import('../purchase-orders/PoDetailPage'));
const PrDetailPage = React.lazy(() => import('../purchase-requisitions/PrDetailPage'));
const BomDetailPage = React.lazy(() => import('../boms/BomDetailPage'));

function DetailFallback() {
  return (
    <div role="status" aria-live="polite">
      <EmptyState className="px-4 py-10" description="Loading…" />
    </div>
  );
}

type SuspenseWrapperProps = {
  children: React.ReactNode;
};

function DetailSuspense({ children }: SuspenseWrapperProps) {
  return <Suspense fallback={<DetailFallback />}>{children}</Suspense>;
}

export function ItemCreateRoute() {
  return (
    <DetailSuspense>
      <ItemDetailPage mode="create" itemId={null} />
    </DetailSuspense>
  );
}

export function ItemEditRoute() {
  const { itemId } = useParams();
  return (
    <DetailSuspense>
      <ItemDetailPage mode="edit" itemId={itemId!} />
    </DetailSuspense>
  );
}

export function ItemDuplicateRoute() {
  const { itemId } = useParams();
  return (
    <DetailSuspense>
      <ItemDetailPage mode="duplicate" itemId={itemId!} />
    </DetailSuspense>
  );
}

export function KitCreateRoute() {
  return (
    <DetailSuspense>
      <KitDetailPage mode="create" kitId={null} />
    </DetailSuspense>
  );
}

export function KitEditRoute() {
  const { kitId } = useParams();
  const location = useLocation();
  return (
    <DetailSuspense>
      <KitDetailPage key={location.key} mode="edit" kitId={kitId ?? null} />
    </DetailSuspense>
  );
}

export function BomCreateRoute() {
  const [params] = useSearchParams();
  const conversion = params.get('convert') === '1';
  return (
    <DetailSuspense>
      <BomDetailPage mode="create" bomId={null} initialConversionOpen={conversion} />
    </DetailSuspense>
  );
}

export function BomEditRoute() {
  const { bomId } = useParams();
  const [params] = useSearchParams();
  const location = useLocation();
  const conversion = params.get('convert') === '1';
  return (
    <DetailSuspense>
      <BomDetailPage key={location.key} mode="edit" bomId={bomId ?? null} initialConversionOpen={conversion} />
    </DetailSuspense>
  );
}

export function PurchaseOrderCreateRoute() {
  return (
    <DetailSuspense>
      <PoDetailPage mode="create" poId={null} />
    </DetailSuspense>
  );
}

export function PurchaseOrderEditRoute() {
  const { poId } = useParams();
  const location = useLocation();
  return (
    <DetailSuspense>
      <PoDetailPage key={location.key} mode="edit" poId={poId ?? null} />
    </DetailSuspense>
  );
}

export function PurchaseRequisitionCreateRoute() {
  const [params] = useSearchParams();
  const conversion = params.get('convert') === '1';
  return (
    <DetailSuspense>
      <PrDetailPage mode="create" prId={null} initialConversionOpen={conversion} />
    </DetailSuspense>
  );
}

export function PurchaseRequisitionEditRoute() {
  const { prId } = useParams();
  const [params] = useSearchParams();
  const location = useLocation();
  const conversion = params.get('convert') === '1';
  return (
    <DetailSuspense>
      <PrDetailPage key={location.key} mode="edit" prId={prId ?? null} initialConversionOpen={conversion} />
    </DetailSuspense>
  );
}
